/**
 * Typed schema and inferred types for the case ontology.
 *
 * Validates the runtime shape of `shared/ontology/case_ontology.json` (shipped
 * by Story uu-2.1) at module load time via Zod, and exports the inferred
 * TypeScript surface that primitive components (EntityCard, PoleTabBar,
 * SpeechActCard, etc.) consume through the typed accessors declared in
 * `components/evidence/shared/ontology-context.tsx`.
 *
 * Design notes:
 * - Top-level `OntologySchema` is strict-by-default — a new top-level field
 *   in the JSON will fail the build, forcing a deliberate schema update.
 *   Nested property bags are loose (default `z.object`) since their shape
 *   evolves more frequently in ontology revisions.
 * - Edge property bags omit the `required` flag in the live JSON, so
 *   `EdgePropertySchema` is intentionally narrower than `PropertySchema`.
 * - The `PipelineDef` `constraints` field is optional because only the
 *   `audio` pipeline declares constraints in the current ontology; the
 *   `document` pipeline omits it.
 */

import { z } from 'zod';

export const PropertySchema = z.object({
  name: z.string(),
  type: z.string(),
  required: z.boolean().optional(),
});

export const EdgePropertySchema = z.object({
  name: z.string(),
  type: z.string(),
});

export const NodeTypeSchema = z.object({
  label: z.string(),
  description: z.string(),
  properties: z.array(PropertySchema),
  sub_labels: z.array(z.string()),
});

export const EdgeTypeSchema = z.object({
  relationship_type: z.string(),
  // `evidentiary` was added in edv-5.7 to close the ontology-side
  // classification deferral footnoted at `ontology-context.tsx:17-19`.
  // `RelationshipRow` renders the inline italicized transcript quote
  // (FR-C19) iff `evidentiary === true` AND the edge instance carries
  // a non-empty `sourceQuote` AND the surface is `where === 'call'`
  // (per UX D-DET-7: document-derived edges never render quotes).
  // See `DECISIONS.md` for the 23-edge-type classification rationale.
  evidentiary: z.boolean(),
  domain: z.string(),
  source_types: z.array(z.string()),
  target_types: z.array(z.string()),
  properties: z.array(EdgePropertySchema),
});

export const PipelineDefSchema = z.object({
  description: z.string(),
  node_types: z.array(z.string()),
  edge_types: z.array(z.string()),
  prompt_preamble: z.string(),
  constraints: z.record(z.string(), z.string()).optional(),
});

export const OntologySchema = z
  .object({
    version: z.string(),
    node_types: z.array(NodeTypeSchema),
    edge_types: z.array(EdgeTypeSchema),
    banned_property_keys: z.array(z.string()),
    pipelines: z.object({
      document: PipelineDefSchema,
      audio: PipelineDefSchema,
    }),
  })
  .strict();

export type Property = z.infer<typeof PropertySchema>;
export type EdgeProperty = z.infer<typeof EdgePropertySchema>;
export type NodeType = z.infer<typeof NodeTypeSchema>;
export type EdgeType = z.infer<typeof EdgeTypeSchema>;
export type PipelineDef = z.infer<typeof PipelineDefSchema>;
export type Ontology = z.infer<typeof OntologySchema>;

/**
 * Canonical POLE(E) category union.
 *
 * Lowercase to match the URL-param schema (Decision 4.5). Display
 * capitalization is owned by presentation primitives.
 *
 * The `entities` member is the catch-all category for ontology labels
 * that are not classified into people / objects / locations / events
 * (e.g., `nc:Organization`, plus any unmapped future label per
 * `poleCategoryFor` in `lib/evidence/pole-categorization.ts`).
 */
export type PoleCategory =
  | 'people'
  | 'objects'
  | 'locations'
  | 'events'
  | 'entities';
